/**
 * natural_nav — the natural bodies a pilot can fly to (2026-10-07). PURE: the makeup and the clock, no view.
 * The sector's nav list (N, the hyperdrive, the autopilot) and the system chart's markers read these.
 */
import { systemSector, type StarSystem } from '../../galaxy.js';
import { moonOffsetAt, orbitAt } from './natural_orbit.js';
import type { MoonMakeup, OrbitMakeup, SystemMakeup } from './natural_types.js';

/** A natural body a pilot can fly to: a nav target and a marker on the system chart. `pos` is refreshed every frame IN PLACE. */
export interface NavBody { name: string; kind: 'comet' | 'moon' | 'dwarf' | 'asteroid' | 'trojans' | 'rogue' | 'visitor' | 'centaur'; pos: [number, number, number]; radius: number; arrive: number }
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const LANDMARK_NAME = { contact_binary: 'Contact binary', binary_asteroid: 'Binary asteroid', active_asteroid: 'Active asteroid', rubble_pile: 'Rubble pile' } as const;

/**
 * The bodies of a makeup that have a place (pure; the views are not asked): where each is at wall-clock second `nowS`.
 * The returned `move` rewrites every moving body's `pos` in place — the sector's nav list and the chart hold the arrays.
 */
export function navBodiesOf(system: StarSystem, makeup: SystemMakeup): { bodies: NavBody[]; move(nowS: number): void } {
  const bodies: NavBody[] = [];
  const movers: Array<(t: number) => void> = [];
  if (system.id === 'sys0') return { bodies, move: () => { /* the Commons Reach's comets are its own points of interest */ } };
  const sec = systemSector(system);
  const scratch: [number, number, number] = [0, 0, 0];
  for (const pm of makeup.planets) {
    const pl = sec.planets[pm.index];
    if (!pl) continue;
    for (const [i, m] of pm.moons.entries()) {
      if (m.shepherd) continue; // a ring's shepherd is a sight, not a destination
      // a planet is "<system> II"; its moons take letters ("<system> II b") — the name the moon view registers its body under
      const b: NavBody = { name: `${pl.name} ${'abcdef'[i] ?? i}`, kind: 'moon', pos: [pl.pos[0], pl.pos[1], pl.pos[2]], radius: m.radiusM, arrive: m.radiusM * 3 + 2_000 };
      const mm: MoonMakeup = m;
      movers.push((t) => { moonOffsetAt(mm, t, scratch); b.pos[0] = pl.pos[0] + scratch[0]; b.pos[1] = pl.pos[1] + scratch[1]; b.pos[2] = pl.pos[2] + scratch[2]; });
      bodies.push(b);
    }
  }
  for (const fm of makeup.fields) if (fm.landmark) bodies.push({ name: LANDMARK_NAME[fm.landmark.kind], kind: 'asteroid', pos: [...fm.landmark.pos], radius: fm.landmark.radiusM, arrive: fm.landmark.radiusM * 3 + 1_500 });
  let comets = 0;
  for (const f of makeup.features) {
    if (f.kind === 'comet' && f.orbit) {
      const o: OrbitMakeup = f.orbit;
      const b: NavBody = { name: `${system.name} comet${comets++ ? ` ${ROMAN[comets - 1]}` : ''}${f.variant === 'dormant' ? ' (dormant)' : ''}`, kind: 'comet', pos: [0, 0, 0], radius: f.nucleusM, arrive: 6_000 };
      movers.push((t) => { orbitAt(o, t, b.pos); });
      bodies.push(b);
    } else if (f.kind === 'centaur') {
      const o: OrbitMakeup = f.orbit;
      const b: NavBody = { name: `${system.name} centaur`, kind: 'centaur', pos: [0, 0, 0], radius: f.radiusM, arrive: f.radiusM * 3 + 2_000 };
      movers.push((t) => { orbitAt(o, t, b.pos); });
      bodies.push(b);
    } else if (f.kind === 'tno_belt' && f.dwarf) {
      bodies.push({ name: `${system.name} Minor`, kind: 'dwarf', pos: [...f.dwarf.pos], radius: f.dwarf.radiusM, arrive: f.dwarf.moonOrbitM * 1.6 });
    } else if (f.kind === 'trojans') {
      const pn = sec.planets[f.planet]?.name ?? 'the giant';
      f.swarms.forEach((sw, i) => bodies.push({ name: `${pn} trojans${f.swarms.length > 1 ? (i === 0 ? ' (leading)' : ' (trailing)') : ''}`, kind: 'trojans', pos: [...sw.pos], radius: sw.radiusM, arrive: sw.radiusM * 1.3 }));
    } else if (f.kind === 'rogue') {
      bodies.push({ name: 'Rogue world', kind: 'rogue', pos: [...f.pos], radius: f.radiusM, arrive: f.radiusM * 4 });
    } else if (f.kind === 'interstellar') {
      const b: NavBody = { name: 'Interstellar visitor', kind: 'visitor', pos: [...f.from], radius: f.lengthM, arrive: 4_000 };
      movers.push((t) => { const u = t / f.periodS + f.phase0, k = u - Math.floor(u); b.pos[0] = f.from[0] + (f.to[0] - f.from[0]) * k; b.pos[1] = f.from[1] + (f.to[1] - f.from[1]) * k; b.pos[2] = f.from[2] + (f.to[2] - f.from[2]) * k; });
      bodies.push(b);
    }
  }
  return { bodies, move: (t) => { for (const m of movers) m(t); } };
}
